/**
 * Date/time helpers for the agenda screens.
 *
 * `<input type="date">` and `<input type="time">` speak the device's local time,
 * and this household is in one place — so a local wall time is turned into a UTC
 * instant with the device's own zone (`new Date('YYYY-MM-DDTHH:MM')` parses as
 * local), and back. The shared week logic buckets by Europe/Amsterdam; on a
 * phone in the Netherlands the two agree, which is the case that matters.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

/** ISO instant → the `YYYY-MM-DD` a date input wants, in local time. */
export function isoToDateInput(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** ISO instant → the `HH:MM` a time input wants, in local time. */
export function isoToTimeInput(iso: string): string {
  const d = new Date(iso);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** A local `date` + `time` (from the inputs) → a UTC ISO instant. */
export function inputsToIso(date: string, time: string): string {
  return new Date(`${date}T${time || '00:00'}`).toISOString();
}

/** Whole minutes between two instants (never negative). */
export function durationMinutes(startIso: string, endIso: string): number {
  return Math.max(0, Math.round((Date.parse(endIso) - Date.parse(startIso)) / 60_000));
}

/** An instant shifted by whole minutes, back as a local date+time pair. */
export function shiftInputs(date: string, time: string, deltaMin: number): { date: string; time: string } {
  const iso = new Date(Date.parse(inputsToIso(date, time)) + deltaMin * 60_000).toISOString();
  return { date: isoToDateInput(iso), time: isoToTimeInput(iso) };
}

/** "1u", "1u 30m", "45m", "1 dag 3u", "2 dagen" — a duration a person reads. */
export function formatDuration(mins: number): string {
  if (mins <= 0) return '0m';
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const minutes = mins % 60;
  const parts: string[] = [];
  if (days) parts.push(days === 1 ? '1 dag' : `${days} dagen`);
  if (hours) parts.push(`${hours}u`);
  if (minutes) parts.push(`${minutes}m`);
  return parts.join(' ');
}

const dayHeadingFmt = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'UTC',
  weekday: 'long',
  day: 'numeric',
  month: 'short',
});

/** A local date key (`YYYY-MM-DD`) → "maandag 17 aug." */
export function formatDayHeading(dateKey: string): string {
  return dayHeadingFmt.format(new Date(`${dateKey}T12:00:00Z`));
}

const timeFmt = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  hour: '2-digit',
  minute: '2-digit',
});

/** "09:00–17:00", "vanaf 09:00", or "Hele dag". */
export function formatTimeRange(startIso: string, endIso: string | null, allDay = false): string {
  if (allDay) return 'Hele dag';
  const start = timeFmt.format(new Date(startIso));
  if (!endIso) return start;
  return `${start}–${timeFmt.format(new Date(endIso))}`;
}

/**
 * The time label for one day of a possibly multi-day occurrence. A day that is
 * only a continuation of a longer span reads as such rather than showing a raw
 * 00:00–00:00 clipped at midnight: a full middle day is "hele dag", the first
 * day "vanaf 09:00 →", the last "→ tot 12:00".
 */
export function formatSpanTime(item: {
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  continuesBefore?: boolean;
  continuesAfter?: boolean;
}): string {
  // The continuation checks come *before* the all-day one, deliberately. A
  // multi-day span is drawn once per day it touches, and each segment has to say
  // which way it runs on — otherwise a three-day trip is three identical "Hele
  // dag" cards with nothing to say they are one event. `continuesBefore` /
  // `continuesAfter` are already computed for every segment; testing `all_day`
  // first (as this did) threw that away for exactly the spans that need it most,
  // while a *timed* span crossing midnight kept its arrows.
  if (item.continuesBefore && item.continuesAfter) return item.all_day ? '→ Hele dag →' : 'Hele dag';
  if (item.continuesBefore) {
    return item.all_day ? '→ Hele dag' : `→ tot ${timeFmt.format(new Date(item.ends_at ?? item.starts_at))}`;
  }
  if (item.continuesAfter) {
    return item.all_day ? 'Hele dag →' : `vanaf ${timeFmt.format(new Date(item.starts_at))} →`;
  }
  if (item.all_day) return 'Hele dag';
  return formatTimeRange(item.starts_at, item.ends_at, item.all_day);
}

const eventDayFmt = new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

/**
 * An ISO **instant** → "wo 20 aug." — which day a stored row falls on.
 *
 * Unlike `formatDayHeading`, which takes a `YYYY-MM-DD` key the week logic has
 * already bucketed, this reads a raw `starts_at`, so it must resolve the
 * household's zone itself rather than the UTC-noon trick.
 */
export function formatEventDay(iso: string): string {
  return eventDayFmt.format(new Date(iso));
}

const stripWeekdayFmt = new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', weekday: 'short' });
const stripNumFmt = new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', day: 'numeric' });

/** A local date key → the two pieces the week-at-a-glance strip shows: "ma" / "17". */
export function formatStripDay(dateKey: string): { weekday: string; num: string } {
  const d = new Date(`${dateKey}T12:00:00Z`);
  return { weekday: stripWeekdayFmt.format(d), num: stripNumFmt.format(d) };
}

const rangeFmt = new Intl.DateTimeFormat('nl-NL', { timeZone: 'UTC', day: 'numeric', month: 'short' });

/** "17 – 23 aug." for the seven day keys of a week. */
export function formatWeekRange(days: string[]): string {
  if (days.length === 0) return '';
  const first = rangeFmt.format(new Date(`${days[0]}T12:00:00Z`));
  const last = rangeFmt.format(new Date(`${days[days.length - 1]}T12:00:00Z`));
  return `${first} – ${last}`;
}

/** The local date key for "today", in the household's timezone. */
export function todayKey(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Amsterdam',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
